<template>
  <v-dialog
    v-model="isOpened"
    persistent
    width="680px"
  >
    <v-form @submit.prevent.native="submit">
      <v-card>
        <v-card-title>
          Nouvel utilisateur
          <v-spacer />
          <v-btn icon @click="isOpened = false">
            <v-icon>clear</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-3">
          <v-alert
            :value="true"
            type="error"
            class="mb-2"
            v-if="errors.length"
          >
            Veuillez corriger les erreurs suivantes :
            <ul>
              <li v-for="(error, i) in errors" :key="`error${i}`">
                {{ error }}
              </li>
            </ul>
          </v-alert>
          <v-container class="pa-2">
            <h3 class="mb-2">Informations générales de l'utilisateur</h3>
            <v-row>
              <v-col cols="12" md="6" class="pa-3">
                <v-text-field v-model="user.firstname" label="Prénom" outlined hide-details autofocus required />
              </v-col>
              <v-col cols="12" md="6" class="pa-3">
                <v-text-field v-model="user.lastname" label="Nom" outlined hide-details required />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="6" class="pa-3">
                <v-text-field v-model="user.email" label="Email" outlined hide-details required />
              </v-col>
              <v-col cols="12" md="6" class="pa-3" v-if="!company.isRoot">
                <v-text-field v-model="user.username" label="Identifiant" outlined hide-details />
              </v-col>
              <v-col cols="12" md="6" class="pa-3" v-else-if="user.Client_id !== company.id">
                <v-select
                  :items="clientUserRights"
                  v-model="user.$right"
                  label="Droit"
                  outlined
                  item-text="label"
                  item-value="id"
                  return-object
                  hide-details
                ></v-select>
              </v-col>
            </v-row>
            <v-row v-if="!company.isRoot">
              <v-col cols="12" md="6" class="pa-3">
                <v-select
                  v-model="user.$right"
                  :items="clientUserRights"
                  label="Rôle"
                  outlined
                  item-text="label"
                  item-value="id"
                  return-object
                  hide-details
                ></v-select>
              </v-col>
            </v-row>
            <template v-if="false">
              <h3 class="mt-5 mb-2 pt-2">Quelle couleur sera assignée à l'utilisateur ?</h3>
              <v-row no-gutters justify="start" align="center">
                <v-menu>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      :color="user.color"
                      v-bind="attrs"
                      v-on="on"
                      style="width: 100px;"
                    >
                      &nbsp;
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item
                      v-for="(color, index) in colors"
                      :key="index"
                    >
                      <v-btn
                        :color="color"
                        @click="selectColor(color)"
                        style="width: 100px;"
                      >
                        &nbsp;
                      </v-btn>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </v-row>
            </template>
            <h3 class="mt-5 mb-2 pt-2">Comment sera défini le mot de passe de l'utilisateur ?</h3>
            <v-radio-group
              v-model="$isSendingEmailForPassword"
              :disabled="hasUsername"
              hide-details
              class="d-flex"
            >
              <v-row no-gutters>
                <v-col>
                  <v-radio
                    :value="true"
                    label="Envoi d'un email (préconisé)"
                    color="primary darken-1"
                  ></v-radio>
                </v-col>
                <v-col>
                  <v-radio
                    :value="false"
                    label="Manuellement"
                    color="primary darken-1"
                  >
                  </v-radio>
                </v-col>
              </v-row>
            </v-radio-group>
            <div class="pt-6" v-if="!$isSendingEmailForPassword">
              <v-alert :value="true" type="warning" v-if="hasUsername">
                L'utilisateur ayant un identifiant, vous devez définir son mot de passe manuellement
              </v-alert>
              <v-alert :value="true" type="info">
                Le mot de passe doit faire 6 caractères minimum
              </v-alert>
              <v-row class="mt-3">
                <v-col cols="12" md="6" class="pa-3">
                  <v-text-field v-model="user.password" label="Mot de passe" type="password" outlined hide-details required />
                </v-col>
                <v-col cols="12" md="6" class="pa-3">
                  <v-text-field v-model="user.repeatPassword" label="Confirmer le mot de passe" type="password" outlined hide-details required />
                </v-col>
              </v-row>
            </div>
          </v-container>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="isOpened = false">Annuler</v-btn>
          <v-btn color="primary darken-1" :loading="isSaving" @click="submit">Ajouter</v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
import { mapState } from 'vuex';
import _ from 'lodash';

const USER_DEFAULT_STATE = {
  clientAdmin: false,
  password: ``,
  repeatPassword: ``,
};

export default {
  name: `NewUserDialog`,
  props: [`value`, `client`],
  data: () => ({
    user: _.clone(USER_DEFAULT_STATE),
    isSaving: false,
    isSendingEmailForPassword: true,
    errors: [],
    clientUserRights: [
      { id: 1, label: `Gestion` },
      { id: 2, label: `Consultation` },
    ],
  }),
  computed: {
    isOpened: {
      get() { return this.value; },
      set(isOpened) { this.$emit(`input`, isOpened); },
    },
    ...mapState(`model/client`, [
      `company`,
    ]),
    ...mapState(`model/user`, [
      `colors`,
    ]),
    $isSendingEmailForPassword: {
      get() { return this.isSendingEmailForPassword && !this.hasUsername; },
      set(value) { this.isSendingEmailForPassword = value; },
    },
    hasUsername() {
      return Boolean(this.user.username && this.user.username.length);
    },
  },
  methods: {
    initLocalState() {
      this.isSaving = false;
      this.isSendingEmailForPassword = true;
      this.user = _.clone(USER_DEFAULT_STATE);
      this.user.color = this.colors[_.random(0, this.colors.length - 1)];
      if (this.company.isRoot) {
        this.user.$right = this.clientUserRights[0]; // eslint-disable-line
      } else {
        this.user.$right = this.clientUserRights[1]; // eslint-disable-line
      }
    },
    selectColor(color) {
      this.user.color = color;
    },
    async submit() {
      this.user.clientAdmin = this.user.$right.id === 1;
      this.errors = [];
      const hasEmail = this.user.email && this.user.email.length;
      const hasUsername = this.user.username && this.user.username.length;
      const hasPassword = this.user.password && this.user.password.length;
      const isPasswordLongEnough = hasPassword && this.user.password.length >= 6;
      const passwordMode = this.$isSendingEmailForPassword ? `mail` : `manual`;
      if (!this.user.firstname || !this.user.firstname.length) { this.errors.push(`Le prénom est obligatoire`); }
      if (!this.user.lastname || !this.user.lastname.length) { this.errors.push(`Le nom est obligatoire`); }
      if (!hasEmail && !hasUsername) { this.errors.push(`Il est obligatoire de renseigner au moins l'email ou l'identifiant`); }
      if (passwordMode === `manual` && !this.$isSendingEmailForPassword && !isPasswordLongEnough) { this.errors.push(`Le mot de passe doit faire 6 caractères minimum`); }
      if (passwordMode === `manual` && isPasswordLongEnough && this.user.password !== this.user.repeatPassword) { this.errors.push(`Les mots de passe ne correspondent pas`); }
      if (!this.errors.length) {
        this.isSaving = true;
        try {
          const newUser = await this.$store.dispatch(`model/user/addUser`, {
            ...this.user,
            Client_id: this.client.id,
            passwordMode,
          });
          this.$emit(`created`, newUser);
          this.isOpened = false;
        } catch (e) {
          if (e.error === `ALREADY_EXISTS`) {
            this.errors.push(`Un utilisateur avec cet email existe déjà`);
          } else {
            this.errors.push(`Impossible de créer cet utilisateur`);
          }
        }
        this.isSaving = false;
      }
    },
  },
  watch: {
    isOpened(isOpened) {
      if (isOpened) {
        this.initLocalState();
      }
    },
  },
};
</script>

<style lang="scss">

</style>
